import { Injectable } from '@angular/core';
import { ApiCallBase } from '../api-lab4u/apiCallBase';
import { LanguageService } from '../language/language.service';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { Router } from '@angular/router';
import { MixpanelService } from '../analytics/mixpanel.service';
import { UserSessionService } from '../user/user.service';
import { User } from 'src/app/models/user.model';
import { catchError, map, switchMap } from 'rxjs/operators';
import { API_V3_URL } from 'src/app/config/config';
import Swal from 'sweetalert2';
import { Observable, throwError } from 'rxjs';
import { TranslocoService } from '@jsverse/transloco';
import { MsalService } from '@azure/msal-angular';
import {
  UserExists_Req,
  UserExists_Res,
} from './models/auth-user-exists.models';
import {
  MicrosoftGraph,
  MicrosoftProfile,
} from './models/auth-microsoft-login.models';

const SCOPES = ['User.Read'];
const GRAPH_ME_URL = 'https://graph.microsoft.com/v1.0/me';

@Injectable({ providedIn: 'root' })
export class AuthService extends ApiCallBase {
  constructor(
    public languageService: LanguageService,
    public http: HttpClient,
    public router: Router,
    private userSessionService: UserSessionService,
    private mixpanelService: MixpanelService,
    private transloco: TranslocoService,
    private msalService: MsalService,
  ) {
    super(languageService, http, router);
  }

  /**
   * Function to register a new user
   * @param user Variable that brings the data completed by the user
   */
  newUser(user: User) {
    const url = `${API_V3_URL}/auth/register`;

    return this.http.post(url, user, this.getHttpOptionsWithoutTokenV3()).pipe(
      map((resp: any) => {
        const message = this.transloco.translate(
          'SIGN-UP.SUCCESSFULLY-SIGN-UP',
          {},
          this.language,
        );
        Swal.fire(message, user.first_name + ' ' + user.last_name, 'success');
        return resp.userId;
      }),
      catchError((err) => {
        if (err.status === 409) {
          const message = this.transloco.translate(
            'SIGN-UP.ERROR.DESCRIPTION-409-ERROR',
            {},
            this.language,
          );
          Swal.fire('Error', message, 'error');
        } else {
          const message = this.transloco.translate(
            'SIGN-UP.ERROR.TITLE',
            {},
            this.language,
          );
          Swal.fire('Error', message, 'error');
        }
        return throwError(() => new Error(err.message));
      }),
    );
  }

  /**
   * Function that through a request post sign/in the user and return their data
   * @param user Variable that brings all the user information
   * @param rememberUser Variable to remember the users e/mail at the localStorage
   */
  login(user: User, rememberUser = false) {
    if (rememberUser && user.email) localStorage.setItem('email', user.email);
    else localStorage.removeItem('email');

    const url = `${API_V3_URL}/auth/login`;

    return this.http.post(url, user, this.getHttpOptionsWithoutTokenV3()).pipe(
      switchMap((resp: any) => {
        user.password = '****';
        this.userId = resp.userId;
        return this.userSessionService
          .saveStorage(resp.userId, resp.token, user)
          .pipe(
            map(() => {
              return true;
            }),
          );
      }),
      catchError((err: HttpErrorResponse) => {
        throw err;
      }),
    );
  }

  /**
   * Runs the MSAL popup flow and resolves the Microsoft Graph profile of the selected account.
   * The email is resolved here so Login and Register always send the same one: the backend
   * finds Microsoft users by email before checking their `third_party_id`.
   * Errors with an `Error` when MSAL returns no account or Graph returns no id.
   */
  microsoftLogin(): Observable<MicrosoftProfile> {
    // MSAL is initialized once at bootstrap (see main.ts)
    return this.msalService
      .loginPopup({ scopes: SCOPES, prompt: 'select_account' })
      .pipe(
        switchMap(({ account }) => {
          if (!account?.username) {
            throw new Error('No Microsoft account returned');
          }
          this.msalService.instance.setActiveAccount(account);

          return this.msalService
            .acquireTokenSilent({ scopes: SCOPES, account })
            .pipe(
              switchMap(({ accessToken }) =>
                this.http.get<MicrosoftGraph>(GRAPH_ME_URL, {
                  headers: { Authorization: `Bearer ${accessToken}` },
                }),
              ),
              map((profile): MicrosoftProfile => {
                if (!profile?.id) {
                  throw new Error('No Microsoft Graph id returned');
                }
                return {
                  graphId: profile.id,
                  email: profile.mail || profile.userPrincipalName,
                  givenName: profile.givenName ?? null,
                  surname: profile.surname ?? null,
                };
              }),
            );
        }),
      );
  }

  /** Check if a User exists in DB based on its id or email
   * @param query Body of the request
   * @param token User Token for validation
   * @returns The Chat Session as an object
   */
  userExists(query: UserExists_Req): Observable<string> {
    const url = `${API_V3_URL}/users/user-exists`;
    const definedQueryEntries = Object.entries(query).filter(
      (
        entry,
      ): entry is [
        string,
        string | number | boolean | readonly (string | number | boolean)[],
      ] => entry[1] !== undefined,
    );
    const queryParameters = new HttpParams({
      fromObject: Object.fromEntries(definedQueryEntries),
    });
    const apiV3Options = {
      ...this.getHttpOptionsWithoutTokenV3(),
      params: queryParameters,
    };

    const res = this.http.get<UserExists_Res>(url, apiV3Options).pipe(
      map((res) => res.user_id),
      catchError((error) => {
        console.error('Error checking if User exists in DB: ', error);
        throw error;
      }),
    );

    return res;
  }

  /**
   * Function to verify the current token of the user.
   * @param userId ID of the current User
   * @param token Current User token
   */
  verifyToken(userId: string, token: string): Observable<boolean> {
    const url = `${API_V3_URL}/auth/verify-token?user_id=${userId}&user_token=${token}`;
    return this.http.get(url, this.getHttpOptionsV3()).pipe(
      map((data: any) => data.response?.is_valid || false),
      catchError((error) => {
        console.error('Error verifying user token: ', error);
        throw error;
      }),
    );
  }

  /**
   * Function that validates a confirmation token to restore the user's password.
   */
  validateResetPasswordHash(hash: string) {
    const url = `${API_V3_URL}/auth/reset-password-hash-validation?hash=${hash}`;
    return this.http.get(url, this.getHttpOptionsWithoutTokenV3());
  }

  /**
   * Function to reset password after hash validation and form data upload.
   */
  resetPassword(body: {
    email: string;
    password: string;
    hash: string;
    language: string;
  }) {
    body.language = this.language === 'es' ? 'ES-LA' : 'EN-US';
    const url = `${API_V3_URL}/auth/reset-password-response`;
    return this.http.patch(url, body, this.getHttpOptionsWithoutTokenV3());
  }

  /**
   * Method to request password reset
   * @param body email and language
   * @returns response or error
   */
  requestPasswordReset(body: { email: string; language: string }) {
    body.language = this.language === 'es' ? 'ES-LA' : 'EN-US';
    const url = `${API_V3_URL}/auth/reset-password-request`;
    return this.http.post(url, body, this.getHttpOptionsWithoutTokenV3()).pipe(
      map((_resp: any) => {
        return _resp;
      }),
      catchError((err) => {
        return throwError(() => new Error(err.message));
      }),
    );
  }

  /**
   * Function that erases all the information (except the language) from the localStorage when signing out
   */
  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('userId');
    localStorage.removeItem('App');
    localStorage.removeItem('settings');
    localStorage.removeItem('new_user_login');
    localStorage.removeItem('planningId');
    sessionStorage.removeItem('mixpanelInitialized');

    // legacy Mixpanel SDK reset() could throw pre-migration; TelemetryRelayService.reset() cannot throw.
    this.mixpanelService.reset();

    window.location.href = '/sign-in';
  }
}
